// External libraries
import { useCallback } from 'react';
import { useStoreActions } from '@/store';
import debounce from 'lodash.debounce';

// Internal modules
import { isFunction } from '@/lib/utils/typeUtils';
import { fetchPhoneMetadata } from './graphql/operations/fetchPhoneMetadata';
import type { InputOnChange } from '@/types';

interface UseChangeHandlerFactoryParam {
  propsChangeHandler: InputOnChange;
}

export const useChangeHandlerFactory = ({
  propsChangeHandler,
}: UseChangeHandlerFactoryParam): InputOnChange => {
  const updatePhoneMetadata = useStoreActions(
    ({ api }) => api.updatePhoneMetadata,
  );

  // Lots of expect errors, because this is used to pass into input on change
  // Which does not have these other params unless something is special in
  // react-components that adds these extra param?
  // TS migration, so not going to risk breaking code changing it
  const changeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    // @ts-expect-error
    value,
    // @ts-expect-error
    oldValue,
    // @ts-expect-error
    meta,
  ) => {
    if (isFunction(propsChangeHandler)) {
      // @ts-expect-error
      propsChangeHandler(e, value, oldValue, meta);
    }

    updatePhoneMetadata({});

    fetchPhoneMetadata(value).then(newPhoneMetadata => {
      updatePhoneMetadata(newPhoneMetadata || {});
    });
  };

  const debouncedChangeHandler = useCallback(
    debounce((e, value, oldValue, meta) => {
      changeHandler(e, value, oldValue, meta);
    }, 100),
    [],
  );

  // @ts-expect-error
  return debouncedChangeHandler;
};
